import { useState } from 'react'
import { Clock, CopyPlus, Pencil } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { StrategyDetail, StrategyTemplate, StrategyView } from '@/lib/api-types'
import { formatDuration, formatFeeTotal, formatMarketCapRange, formatPrice, formatRawTokenAmount, formatTime, formatUsd } from '@/lib/format'
import { StatusBadge, StrategyTitle } from './strategy-card'
import { Modal } from './modal'
import { DetailList } from './detail-list'
import { Spinner } from './spinner'
import { TokenIcon } from './holdings-table'
import { tokenLogo } from '@/lib/default-tokens'
import { CustodyLogo } from './custody-logo'
import { CUSTODY_LABELS } from '@/lib/pool'
import { MarketCapDialog } from './market-cap-dialog'

export function StrategyDetailModal({
  id,
  onClose,
  onDuplicate,
}: {
  id: string | null
  onClose: () => void
  /** open the wizard filled in with this strategy (John, Nov 3 2026): run it again, or change it a little first */
  onDuplicate?: (strategy: StrategyTemplate) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [editingMarketCap, setEditingMarketCap] = useState(false)
  const detail = useQuery({
    ...trpc.strategy.get.queryOptions({ id: id ?? '' }),
    enabled: id !== null,
    refetchInterval: 10_000,
  })
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.strategy.list.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.strategy.get.queryKey({ id: id ?? '' }) })
  }
  const pause = useMutation(trpc.strategy.pause.mutationOptions({ onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) }))
  const resume = useMutation(trpc.strategy.resume.mutationOptions({ onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) }))
  const cancel = useMutation(trpc.strategy.cancel.mutationOptions({ onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) }))
  const s = detail.data
  const settled = s?.status === 'completed' || s?.status === 'cancelled'
  const inputDecimals = s ? (s.side === 'sell' ? s.token.decimals : s.settlement.decimals) : 0
  const outputDecimals = s ? (s.side === 'sell' ? s.settlement.decimals : s.token.decimals) : 0
  // the two sides of an order, with their logos: a sell pays the token and receives the settlement coin
  const token = s ? { symbol: (s.token.symbol ?? 'token').toUpperCase(), imageUrl: tokenLogo(s.token.address, s.token.imageUrl ?? undefined) } : null
  const settlement = s ? { symbol: s.settlement.symbol.toUpperCase(), imageUrl: tokenLogo(s.settlement.address) } : null
  const inToken = s?.side === 'sell' ? token : settlement
  const outToken = s?.side === 'sell' ? settlement : token
  return (
    <Modal
      open={id !== null}
      // Escape and a click outside belong to the market cap dialog while it is open
      onClose={() => {
        if (!editingMarketCap) onClose()
      }}
      title={
        s && token ? (
          <span className="flex items-center gap-2.5">
            <TokenIcon symbol={token.symbol} imageUrl={token.imageUrl} size="size-6" />
            <StrategyTitle strategy={s as StrategyView} />
          </span>
        ) : (
          'Strategy'
        )
      }
      width="xl"
      footer={
        s ? (
          <div className="flex gap-3">
            {/* a copy to run again (John, Nov 3 2026): the wizard opens filled in, nothing starts until it is set there; the one
                action a finished strategy has left, so it leads there */}
            {onDuplicate ? (
              <button type="button" className={`btn flex-1 ${settled ? 'btn-primary' : 'btn-secondary'}`} onClick={() => onDuplicate(s)}>
                <CopyPlus className="size-4" aria-hidden="true" />
                Duplicate
              </button>
            ) : null}
            {s.status === 'active' || s.status === 'scheduled' ? (
              <button type="button" className="btn btn-secondary flex-1" disabled={pause.isPending} onClick={() => pause.mutate({ id: s.id })}>Pause</button>
            ) : null}
            {s.status === 'paused' || s.status === 'failed' ? (
              <button type="button" className="btn btn-primary flex-1" disabled={resume.isPending} onClick={() => resume.mutate({ id: s.id })}>Resume</button>
            ) : null}
            {!settled ? (
              <button type="button" className="btn btn-secondary flex-1 text-negative" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: s.id })}>Stop</button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {!s ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 gap-5 text-sm">
          <div className="flex flex-wrap items-center gap-3">
            {/* the exchange the strategy belongs to: its logo alone, fixed for the strategy's life (John, Nov 2 2026: never clickable once created) */}
            {s.exchange ? (
              <span className="chip flex size-8 cursor-default items-center justify-center !rounded-lg !p-0" role="img" title={CUSTODY_LABELS[s.exchange]} aria-label={CUSTODY_LABELS[s.exchange]}>
                <CustodyLogo custody={s.exchange} size="size-5" />
              </span>
            ) : null}
            <StatusBadge status={s.status} />
            <span className="text-muted">
              {s.kind === 'limit' ? (s.status === 'completed' ? 'Filled' : s.status === 'active' ? 'Waiting for the price' : '') : `${s.confirmedOrders}/${s.dca.orders} orders filled`}
            </span>
            <span className="ml-auto">
              <StrategyName strategy={s as StrategyView} onSaved={invalidate} />
            </span>
          </div>
          {s.isPrivate && s.status === 'scheduled' && !s.nextRunAt ? <p className="text-xs text-muted">Waiting for the funding to land…</p> : null}
          {s.kind === 'limit' ? (
            <LimitSummary strategy={s} inToken={inToken!} inputDecimals={inputDecimals} />
          ) : (
            <>
              <PlanSummary strategy={s} inToken={inToken!} outToken={outToken!} inputDecimals={inputDecimals} outputDecimals={outputDecimals} />
              <OrderRail strategy={s} />
            </>
          )}
          <DetailList
            rows={
              s.kind === 'limit'
                ? [
                    { label: s.side === 'sell' ? 'Receive' : 'Pay with', value: <TokenValue token={settlement!} /> },
                    { label: 'Price impact limit', value: `${s.limits.maxPriceImpactPct}%` },
                    { label: 'Last check', value: s.limit?.lastCheckedAt ? formatTime(s.limit.lastCheckedAt) : null },
                    { label: 'Fees so far', value: formatFeeTotal(s.fees) },
                  ]
                : [
                    {
                      label: 'Per order',
                      value: `${formatRawTokenAmount(s.dca.perOrderAmountRaw, inputDecimals, inToken?.symbol)} ${inToken?.symbol}`,
                      hint: s.execution.randomizeAmount ? `±${s.execution.amountVariancePct ?? 40}%` : null,
                    },
                    { label: 'Price impact limit', value: `${s.limits.maxPriceImpactPct}%` },
                    {
                      label: 'Market cap',
                      value:
                        s.status === 'completed' || s.status === 'cancelled' ? (
                          formatMarketCapRange(s.limits.minMarketCapUsd, s.limits.maxMarketCapUsd)
                        ) : (
                          <button type="button" className="inline-flex items-center gap-1.5 hover:text-accent" onClick={() => setEditingMarketCap(true)} aria-label="Edit the market cap range">
                            {formatMarketCapRange(s.limits.minMarketCapUsd, s.limits.maxMarketCapUsd)}
                            <Pencil className="size-3.5 text-muted" aria-hidden="true" />
                          </button>
                        ),
                    },
                    { label: 'Fees so far', value: formatFeeTotal(s.fees) },
                  ]
            }
          />
          {s.lastError ? <p className="break-words text-negative">{s.lastError}</p> : null}
          {error ? <p className="break-words text-negative">{error}</p> : null}
          <MarketCapDialog strategy={s} open={editingMarketCap} onClose={() => setEditingMarketCap(false)} onSaved={invalidate} />
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-muted">Executions</div>
            {s.executions.length === 0 ? (
              <p className="text-muted">No orders have run yet.</p>
            ) : (
              <ul className="min-w-0 divide-y divide-line">
                {s.executions.map((e) => (
                  <li key={e.id} className="flex items-start justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="tabular-nums">
                          #{e.index + 1}
                          {e.attempt > 0 ? `.${e.attempt + 1}` : ''}
                        </span>
                        <ExecutionStatus status={e.status} detail={e.skipReason ?? e.error ?? null} />
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
                        <span className="whitespace-nowrap">{formatTime(e.scheduledAt)}</span>
                        {e.transactionId ? (
                          <a className="text-accent" href={`https://solscan.io/tx/${e.transactionId}`} target="_blank" rel="noreferrer">
                            View
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                        −{formatRawTokenAmount(e.amountRaw, inputDecimals, inToken!.symbol)}
                        <TokenIcon symbol={inToken!.symbol} imageUrl={inToken!.imageUrl} size="size-4" />
                        {inToken!.symbol}
                      </div>
                      {e.outputAmountRaw ? (
                        <div className="mt-0.5 flex items-center justify-end gap-1 whitespace-nowrap text-positive">
                          +{formatRawTokenAmount(e.outputAmountRaw, outputDecimals, outToken!.symbol)}
                          <TokenIcon symbol={outToken!.symbol} imageUrl={outToken!.imageUrl} size="size-4" />
                          {outToken!.symbol}
                          {e.valueUsd !== null ? <span className="text-muted">({formatUsd(e.valueUsd)})</span> : null}
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

/** the optional label under the card title: shown with a pencil, edited in place, cleared by saving it empty */
function StrategyName({ strategy: s, onSaved }: { strategy: StrategyView; onSaved: () => void }) {
  const trpc = useTRPC()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(s.name ?? '')
  const rename = useMutation(
    trpc.strategy.rename.mutationOptions({
      onSuccess: () => {
        setEditing(false)
        onSaved()
      },
    }),
  )
  if (!editing) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text"
        onClick={() => {
          setValue(s.name ?? '')
          setEditing(true)
        }}
      >
        {s.name ? <span className="max-w-40 truncate">{s.name}</span> : <span>Name</span>}
        <Pencil className="size-3.5" />
      </button>
    )
  }
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        rename.mutate({ id: s.id, name: value.trim() })
      }}
    >
      <div className="control flex items-center px-2">
        <input className="w-32 bg-transparent py-1 text-sm outline-none" maxLength={24} placeholder="Name" value={value} onChange={(e) => setValue(e.target.value)} />
      </div>
      <button type="submit" className="btn btn-primary !px-3 !py-1 text-sm" disabled={rename.isPending}>
        Save
      </button>
      <button type="button" className="btn btn-ghost !px-2 !py-1 text-sm" onClick={() => setEditing(false)}>
        Cancel
      </button>
    </form>
  )
}

/** the order's state in a word, with the reason when it did not fill */
function ExecutionStatus({ status, detail }: { status: string; detail: string | null }) {
  const bad = status === 'failed' || status === 'skipped' || detail !== null
  return (
    <span className={`min-w-0 break-words ${bad ? 'text-negative' : 'text-muted'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
      {detail ? `: ${detail}` : ''}
    </span>
  )
}

type SummaryToken = { symbol: string; imageUrl?: string }

/** how much of the plan has gone into orders (a failed order's amount comes back): the same figure as the card's bar */
function planDone(s: StrategyDetail) {
  const total = BigInt(s.dca.totalAmountRaw)
  if (total <= 0n) return 0n
  const remaining = BigInt(s.remainingAmountRaw)
  return remaining <= 0n ? total : remaining >= total ? 0n : total - remaining
}

/**
 * The plan's two sides at a glance (John, Nov 1 2026: the grid of stat tiles was ugly): what has gone out and what
 * has come in, each a large figure with its dollar value, then what is left and the average price the orders got.
 */
function PlanSummary({
  strategy: s,
  inToken,
  outToken,
  inputDecimals,
  outputDecimals,
}: {
  strategy: StrategyDetail
  inToken: SummaryToken
  outToken: SummaryToken
  inputDecimals: number
  outputDecimals: number
}) {
  const done = planDone(s)
  const total = BigInt(s.dca.totalAmountRaw)
  const remaining = BigInt(s.remainingAmountRaw)
  const totalText = `${formatRawTokenAmount(total, inputDecimals, inToken.symbol)} ${inToken.symbol}`
  const left =
    remaining <= 0n
      ? `All ${totalText} ${s.side === 'sell' ? 'sold' : 'spent'}`
      : `${formatRawTokenAmount(remaining, inputDecimals, inToken.symbol)} of ${totalText} left`
  const received = s.receivedAmountRaw === null ? null : BigInt(s.receivedAmountRaw)
  return (
    <div className="grid grid-cols-2 divide-x divide-line">
      <SummaryHalf
        className="pr-4"
        token={inToken}
        amount={done > 0n ? `−${formatRawTokenAmount(done, inputDecimals, inToken.symbol)}` : '0'}
        usd={done > 0n ? s.filledUsd : null}
        note={left}
      />
      <SummaryHalf
        className="pl-4"
        token={outToken}
        amount={received === null ? '–' : received > 0n ? `+${formatRawTokenAmount(received, outputDecimals, outToken.symbol)}` : '0'}
        usd={s.receivedUsd}
        note={s.avgPriceUsd !== null ? `Avg price ${formatPrice(s.avgPriceUsd)}` : s.confirmedOrders === 0 ? 'No orders have filled yet' : null}
      />
    </div>
  )
}

/** a limit order at a glance: the amount it moves, then the target against the price now (or the fill price) */
function LimitSummary({ strategy: s, inToken, inputDecimals }: { strategy: StrategyDetail; inToken: SummaryToken; inputDecimals: number }) {
  const target = s.limit?.targetPriceUsd ?? null
  const last = s.limit?.lastPriceUsd ?? null
  const toGo = target !== null && last !== null && last > 0 ? ((target - last) / last) * 100 : null
  const filled = s.status === 'completed'
  const priceNote = filled
    ? s.avgPriceUsd !== null
      ? `Filled at ${formatPrice(s.avgPriceUsd)}`
      : 'Filled'
    : last !== null
      ? `Now ${formatPrice(last)}${toGo !== null ? ` (${toGo > 0 ? '+' : ''}${toGo.toFixed(1)}% to go)` : ''}`
      : s.status === 'active'
        ? 'Checking the price…'
        : null
  return (
    <div className="grid grid-cols-2 divide-x divide-line">
      <SummaryHalf
        className="pr-4"
        token={inToken}
        amount={formatRawTokenAmount(s.dca.totalAmountRaw, inputDecimals, inToken.symbol)}
        usd={filled ? s.filledUsd : s.perOrderUsd}
        note={filled ? 'Filled' : s.status === 'active' ? 'Waiting for the price' : null}
      />
      <div className="min-w-0 pl-4">
        <div className="text-xs text-muted">Target</div>
        <div className="mt-1 text-xl font-semibold tabular-nums sm:text-2xl">{formatPrice(target)}</div>
        {priceNote ? <div className="mt-0.5 text-xs text-muted tabular-nums">{priceNote}</div> : null}
      </div>
    </div>
  )
}

/** one side of the summary: the token above, the figure with its dollar value, a line of context under it */
function SummaryHalf({ token, amount, usd, note, className = '' }: { token: SummaryToken; amount: string; usd: number | null; note: string | null; className?: string }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <TokenIcon symbol={token.symbol} imageUrl={token.imageUrl} size="size-4" />
        {token.symbol}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-xl font-semibold tabular-nums sm:text-2xl">{amount}</span>
        {usd !== null && usd > 0 ? <span className="tabular-nums text-muted">{formatUsd(usd)}</span> : null}
      </div>
      {note ? <div className="mt-0.5 text-xs tabular-nums text-muted">{note}</div> : null}
    </div>
  )
}

/** a plan longer than this shares its segments between orders */
const RAIL_SEGMENTS = 48

/**
 * The orders as a row of segments that fill as they confirm (a lighter one is in flight), the cadence above,
 * the order up next and when it runs below. Settled plans say what happened instead.
 */
function OrderRail({ strategy: s }: { strategy: StrategyDetail }) {
  const orders = Math.max(1, s.dca.orders)
  const confirmed = Math.min(orders, s.confirmedOrders)
  const inFlight = Math.min(orders - confirmed, s.executions.filter((e) => e.status === 'sent' || e.status === 'collecting').length)
  const segments = Math.min(orders, RAIL_SEGMENTS)
  const share = (n: number) => (n <= 0 ? 0 : Math.max(1, Math.round((n / orders) * segments)))
  const filledSegments = confirmed >= orders ? segments : Math.min(segments - 1, share(confirmed))
  const flightSegments = Math.min(segments - filledSegments, share(inFlight))
  const settled = s.status === 'completed' || s.status === 'cancelled'
  const side = s.side === 'sell' ? 'sells' : 'buys'
  const title = settled ? side.charAt(0).toUpperCase() + side.slice(1) : `Scheduled ${side}`
  const cadence = `Every ${formatDuration(s.dca.intervalSeconds)}${s.execution.randomizeInterval ? ` ±${s.execution.intervalVariancePct ?? 40}%` : ''}`
  const running = s.status === 'active' || s.status === 'scheduled'
  const count =
    s.status === 'completed'
      ? `All ${orders} ${orders === 1 ? 'order' : 'orders'} filled`
      : running && inFlight > 0
        ? `Order ${Math.min(orders, confirmed + 1)} of ${orders} filling`
        : running
          ? `Order ${Math.min(orders, confirmed + 1)} of ${orders} next`
          : `${confirmed} of ${orders} filled`
  const when =
    s.status === 'active' && s.nextRunAt
      ? `Next ${formatTime(s.nextRunAt)}`
      : s.status === 'scheduled' && s.nextRunAt
        ? `Starts ${formatTime(s.nextRunAt)}`
        : s.status === 'completed' && s.finishedAt
          ? `Finished ${formatTime(s.finishedAt)}`
          : s.status === 'cancelled' && s.finishedAt
            ? `Stopped ${formatTime(s.finishedAt)}`
            : null
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 font-medium">
          <Clock className="size-4 text-muted" aria-hidden="true" />
          {title}
        </span>
        <span className="text-muted">{cadence}</span>
      </div>
      <div
        className="mt-2.5 flex gap-0.5"
        role="progressbar"
        aria-valuenow={confirmed}
        aria-valuemin={0}
        aria-valuemax={orders}
        aria-label={`${confirmed} of ${orders} orders filled`}
      >
        {Array.from({ length: segments }, (_, i) => (
          <span
            key={i}
            className={`h-2 min-w-0 flex-1 rounded-xs ${i < filledSegments ? 'bg-accent' : i < filledSegments + flightSegments ? 'bg-accent/40' : 'bg-surface-3'}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs tabular-nums text-muted">
        <span>{count}</span>
        {when ? <span>{when}</span> : null}
      </div>
    </div>
  )
}

/** a token as it reads in a row: its logo, then the ticker */
function TokenValue({ token }: { token: SummaryToken }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <TokenIcon symbol={token.symbol} imageUrl={token.imageUrl} size="size-4" />
      {token.symbol}
    </span>
  )
}

